import * as React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Copy, Clock, CheckCircle, Link2 } from "lucide-react"
import { toast } from "sonner"
import type { Group } from "@/types/domain.types"
import { logger } from '@/lib/logger'

interface InvitationCode {
  code: string
  groupName: string
  groupId: string
  expiresAt?: string
  createdAt: string
}

interface InvitationCodesListProps {
  groups: Group[]
  onRefreshCode?: (groupId: string) => Promise<string>
}

export function InvitationCodesList({ groups, onRefreshCode }: InvitationCodesListProps) {
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null)

  // Extract invitation codes from groups
  const invitationCodes: InvitationCode[] = React.useMemo(() => {
    const codes: InvitationCode[] = []

    groups.forEach(group => {
      if (group.invitations && Array.isArray(group.invitations)) {
        group.invitations.forEach((invitation: any) => {
          codes.push({
            code: invitation.token,
            groupName: group.name,
            groupId: group.id,
            expiresAt: invitation.expires_at,
            createdAt: invitation.created_at
          })
        })
      }
    })

    return codes
  }, [groups])

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      toast.success("Invitation code copied to clipboard")

      // Reset after 2 seconds
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (err) {
      toast.error("Failed to copy code")
    }
  }

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false

    // Handle various date formats from backend
    let expDate: Date

    if (typeof expiresAt === 'string') {
      // If already has timezone indicator, use as-is
      if (expiresAt.includes('Z') || expiresAt.includes('+')) {
        expDate = new Date(expiresAt)
      } else {
        // Assume UTC if no timezone indicator
        expDate = new Date(expiresAt + 'Z')
      }
    } else {
      // If it's already a Date object or something else
      expDate = new Date(expiresAt)
    }

    if (isNaN(expDate.getTime())) {
      logger.error('[isExpired] Invalid date:', expiresAt)
      return false
    }

    // Compare with current time
    const now = new Date()
    return expDate.getTime() < now.getTime()
  }

  const formatDate = (dateString: string) => {
    // Handle various date formats from backend
    let date: Date

    if (typeof dateString === 'string') {
      // If already has timezone indicator (Z or +00:00), use as-is
      if (dateString.includes('Z') || dateString.includes('+')) {
        date = new Date(dateString)
      } else {
        // Assume UTC if no timezone indicator
        date = new Date(dateString + 'Z')
      }
    } else {
      date = new Date(dateString)
    }

    if (isNaN(date.getTime())) {
      logger.error('[formatDate] Invalid date:', dateString)
      return 'Invalid date'
    }

    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffHours = Math.abs(diffMs) / (1000 * 60 * 60)
    const diffDays = Math.abs(diffMs) / (1000 * 60 * 60 * 24)

    // If less than 24 hours, show hours
    if (diffHours < 24) {
      if (diffMs < 0) {
        return `Expired ${Math.round(diffHours)} hours ago`
      }
      return `Expires in ${Math.round(diffHours)} hours`
    }

    // If less than 7 days, show days
    if (diffDays < 7) {
      if (diffMs < 0) {
        return `Expired ${Math.round(diffDays)} days ago`
      }
      return `Expires in ${Math.round(diffDays)} days`
    }

    // Otherwise show full date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (invitationCodes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Your Invitation Codes
          </CardTitle>
          <CardDescription>
            Share these codes with others to invite them to your groups
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <p>No active invitation codes</p>
            <p className="text-sm mt-2">
              Create invitation codes from your groups to share with others
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          Your Invitation Codes
        </CardTitle>
        <CardDescription>
          Share these codes with others to invite them to your groups
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {invitationCodes.map((invitation, index) => {
            const expired = isExpired(invitation.expiresAt)

            return (
              <div
                key={`${invitation.groupId}-${invitation.code}-${index}`}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  expired ? 'bg-muted/50 opacity-60' : 'bg-card'
                }`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{invitation.groupName}</span>
                    {expired && (
                      <Badge variant="secondary" className="text-xs">
                        Expired
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <code className="break-all font-mono bg-muted px-2 py-1 rounded">
                      {invitation.code}
                    </code>
                    {invitation.expiresAt ? (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(invitation.expiresAt)}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-3 w-3" />
                        Never expires
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!expired && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(invitation.code)}
                      className="gap-2"
                    >
                      {copiedCode === invitation.code ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  )}
                  {expired && onRefreshCode && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRefreshCode(invitation.groupId)}
                    >
                      Refresh Code
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}